import { FC, useState } from "react";
import { Button, Modal, Text } from "rizzui";
import { toast } from "sonner";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../atom/atom";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import CustomSelect from "../../../components/forms/CustomSelect";
import { handleConfirmationAlert } from "../../../components/modal/ConfirmationAlert";
import { HeadquarterService } from "../../../services/headquarters/headquarterService";

interface HeadquarterDeleteAllModalProps {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}

const HeadquarterDeleteAllModal: FC<HeadquarterDeleteAllModalProps> = ({ isOpen, onClose, refetch }) => {

    // STATES
    const [company, setCompany] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useAtom(formsSubmitAtom);

    // HOOKS
    const service = new HeadquarterService();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const { getCompaniesList } = useCompanies();

    // CONSTANTS
    const isSuperAdmin = userCan('list', 'company');
    const noCompanySelected = isSuperAdmin && !company;

    // FUNCTIONS

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES PERFORMA LA ELIMINACIÓN DE TODOS LAS SEDES
     * @EN PERFORMS THE DELETION OF ALL HEADQUARTERS
     */
    // -------------------------------------------------------------------------------------------------------------
    const performDelete = async () => {
        setIsSubmitting(true);
        try {
            const response = await (await service.deleteAllHeadquarters(company || null)).getResponseData();
            if (response.success) {
                toast.success("Todos las sedes han sido eliminadas con éxito");
                refetch();
                onClose();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSubmitting(false);
        }
    };
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL CLICK DEL BOTÓN DE ELIMINACIÓN
     * @EN HANDLES THE CLICK OF THE DELETE BUTTON
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleDeleteClick = () => {
        const allCompaniesWarning = noCompanySelected
            ? `<p class="mt-3 text-sm leading-relaxed"><strong>Atención:</strong> eres superadministrador y no has seleccionado ninguna compañía. Se eliminarán <strong>todos las sedes de todas las compañías</strong>.</p>`
            : '';

        const scopeNote = isSuperAdmin && company
            ? `<p class="mt-2 text-sm text-gray-600">Solo se eliminarán las sedes de la compañía seleccionada.</p>`
            : '';

        handleConfirmationAlert({
            title: '¿Eliminar todos las sedes?',
            icon: 'warning',
            html: `
                <div class="text-start text-sm text-gray-700">
                    <p class="leading-relaxed">¿Estás seguro de que quieres <strong>borrar todas las sedes</strong>?</p>
                    <p class="mt-2 leading-relaxed">Se borrarán todos sus datos asociados y <strong>no habrá vuelta atrás</strong>.</p>
                    ${scopeNote}
                    ${allCompaniesWarning}
                </div>
            `,
            onConfirm: () => {
                void performDelete();
            },
        });
    };
    // -------------------------------------------------------------------------------------------------------------


    // RENDER
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="p-6">
                <div className="mb-6">
                    <Text className="text-xl font-semibold">Eliminar todas las sedes</Text>
                    <Text className="text-gray-500 mt-2">
                        {isSuperAdmin ? (
                            <>
                                Puedes elegir una compañía para limitar el borrado a sus sedes.
                                <br />
                                <span className="text-red-500"><strong>¡ATENCIÓN!</strong> Esta acción es irreversible.</span>
                            </>
                        ) : (
                            <>
                                Se eliminarán todas las sedes aplicables a tu cuenta.
                                <br />
                                <span className="text-red-500"><strong>¡ATENCIÓN!</strong> Esta acción es irreversible.</span>
                            </>
                        )}
                    </Text>
                    {isSuperAdmin && (
                        <div
                            role="status"
                            className="mt-4 flex gap-3 rounded-md border border-gray-200 border-l-[6px] border-l-blue-600 bg-sky-50/90 px-4 py-3.5 text-sm text-gray-700 shadow-sm"
                        >
                            <HiOutlineInformationCircle className="h-5 w-5 shrink-0 text-blue-600" aria-hidden />
                            <p className="leading-relaxed m-0">
                                Si no seleccionas compañía, al confirmar el borrado se eliminarán las sedes de <strong>todas</strong> las compañías.
                            </p>
                        </div>
                    )}
                </div>

                {isSuperAdmin && (
                    <div className="mb-6">
                        <CustomSelect
                            isSearchable
                            isClearable
                            id="company"
                            label="Compañía"
                            placeholder="Todas las compañías"
                            options={getCompaniesList() || []}
                            value={getCompaniesList().find((item: any) => item.value === company) ?? null}
                            onChange={(e: any) => setCompany(e?.value ?? '')}
                        />
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                        Cancelar
                    </Button>
                    <Button onClick={handleDeleteClick} isLoading={isSubmitting}>
                        Borrar todas las sedes
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default HeadquarterDeleteAllModal;
